import { ProvideAuth } from 'hooks/useFirebaseAuth';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { ReactReduxFirebaseProvider } from 'react-redux-firebase';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { GlobalStyles, main } from 'styles';
import { shouldForwardProp } from 'styles/shouldForwardProp';
import App from './App';
import { GOOGLE_TAG_MANAGER_ID, REACT_GA_TRACKING_CODE } from './constants/Constants';
import './i18n';
import { MaybePostHogProvider } from './providers/PostHogProviderWrapper';
import { rrfProps, store } from './store/configStore';
import { sentrySetup } from './utils/sentry/sentrySetup';
import { initGatewayConfig } from './services/gatewayConfig.service';
import { isChunkLoadError, recoverFromChunkLoadError } from './utils/monitoring/chunkLoadError';

import '@nimbly-technologies/audit-component/dist/index.esm.css';
import { ReactQueryClientProvider } from 'core/infrastructure/reactQuery/provider';
import 'swiper/css/bundle';
import './styles/tailwindoutput.css';

ReactGA.initialize(REACT_GA_TRACKING_CODE, {
	debug: false,
});

TagManager.initialize({
	gtmId: GOOGLE_TAG_MANAGER_ID,
});

sentrySetup();

// Kick off remote gateway config fetch (non-blocking; falls back to env vars until resolved)
initGatewayConfig();

// Recover from stale-deploy chunk load failures (AUDIT-ADMIN-1KV) instead of a hard crash
window.addEventListener('error', (event) => {
	if (isChunkLoadError(event.message)) {
		recoverFromChunkLoadError(event.error ?? new Error(event.message));
	}
});
window.addEventListener('unhandledrejection', (event) => {
	const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
	if (isChunkLoadError(message)) {
		recoverFromChunkLoadError(event.reason);
	}
});

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');
container.innerHTML = '';
const root = createRoot(container);

root.render(
	<StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
		<ThemeProvider theme={main}>
			<>
				<GlobalStyles />
				<MaybePostHogProvider>
					<ReactQueryClientProvider>
						<ProvideAuth>
							<Provider store={store}>
								<ReactReduxFirebaseProvider {...rrfProps}>
									<App />
								</ReactReduxFirebaseProvider>
							</Provider>
						</ProvideAuth>
					</ReactQueryClientProvider>
				</MaybePostHogProvider>
			</>
		</ThemeProvider>
	</StyleSheetManager>,
);
